import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { Geopoint as Interface } from '../../design/components';
import { parseGeopoint, formatGeopoint, initLeafletIcon, DEFAULT_CENTER } from '../shared/geopoint';

initLeafletIcon();

async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'User-Agent': 'mksys' } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.display_name || null;
  } catch {
    return null;
  }
}

interface AddressSuggestion {
  label: string;
  lat: number;
  lng: number;
}

async function searchAddresses(query: string): Promise<AddressSuggestion[]> {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5`,
      { headers: { 'User-Agent': 'mksys' } }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return data.map((item: { display_name: string; lat: string; lon: string }) => ({
      label: item.display_name,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
    }));
  } catch {
    return [];
  }
}

function MapClickHandler({ onLocationChange }: { onLocationChange: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapViewUpdater({ center }: { center: [number, number] }) {
  const map = useMap();
  const prevCenter = useRef<[number, number] | null>(null);

  useEffect(() => {
    if (prevCenter.current && (prevCenter.current[0] !== center[0] || prevCenter.current[1] !== center[1])) {
      map.setView(center);
    }
    prevCenter.current = center;
  }, [center, map]);

  return null;
}

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '16px',
  border: '1px solid #ccc',
  boxSizing: 'border-box' as const,
};

export const Geopoint: React.FC<Interface> = ({
  name,
  description,
  state,
  hidden,
  readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [addressInput, setAddressInput] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const position = parseGeopoint(value) || DEFAULT_CENTER;
  const skipReverseGeocode = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reverse geocode: coordinates → address
  useEffect(() => {
    if (skipReverseGeocode.current) {
      skipReverseGeocode.current = false;
      return;
    }
    const coords = parseGeopoint(value);
    if (coords) {
      reverseGeocode(coords[0], coords[1]).then((addr) => {
        if (addr) setAddressInput(addr);
      });
    }
  }, [value]);

  const handleLocationChange = (lat: number, lng: number) => {
    if (readonly || disabled) return;
    setValue(formatGeopoint(lat, lng));
  };

  const handleCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setAddressInput(newValue);
    setShowSuggestions(true);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      if (newValue.length >= 3) {
        const results = await searchAddresses(newValue);
        setSuggestions(results);
      } else {
        setSuggestions([]);
      }
    }, 300);
  };

  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    skipReverseGeocode.current = true;
    setAddressInput(suggestion.label);
    setValue(formatGeopoint(suggestion.lat, suggestion.lng));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div style={{ marginBottom: '1rem', display: hidden ? 'none' : undefined }}>
      <label htmlFor={name} style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
        {name}
        {required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <input
        id={name}
        name={name}
        value={value}
        onChange={handleCoordinateChange}
        disabled={disabled}
        readOnly={readonly}
        placeholder="lat,lng (e.g., 40.7128,-74.0060)"
        style={{
          ...inputStyle,
          borderRadius: '4px 4px 0 0',
          borderBottom: 'none',
          borderColor: error ? 'red' : '#ccc',
        }}
      />
      <div ref={wrapperRef} style={{ position: 'relative' }}>
        <input
          id={`${name}-address`}
          value={addressInput}
          onChange={handleAddressInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          disabled={disabled}
          readOnly={readonly}
          placeholder="Search address..."
          style={{
            ...inputStyle,
            borderRadius: '0 0 4px 4px',
            borderColor: error ? 'red' : '#ccc',
          }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            background: 'white',
            border: '1px solid #ccc',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            {suggestions.map((suggestion) => (
              <li
                key={`${suggestion.lat},${suggestion.lng}`}
                onClick={() => handleSuggestionSelect(suggestion)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  fontSize: '14px',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
              >
                {suggestion.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      {(error || description) && (
        <small style={{ color: error ? 'red' : '#666', display: 'block', marginTop: '4px' }}>
          {error || description}
        </small>
      )}
      <div style={{
        height: 300,
        marginTop: '8px',
        borderRadius: '4px',
        overflow: 'hidden',
        border: error ? '1px solid red' : '1px solid #ccc',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled || readonly ? 'none' : 'auto',
      }}>
        <MapContainer
          center={position}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={position}
            draggable={!readonly && !disabled}
            eventHandlers={{
              dragend: (e) => {
                const marker = e.target;
                const pos = marker.getLatLng();
                handleLocationChange(pos.lat, pos.lng);
              },
            }}
          />
          <MapClickHandler onLocationChange={handleLocationChange} />
          <MapViewUpdater center={position} />
        </MapContainer>
      </div>
    </div>
  );
};
